/**
 * Configuración versionada de la calculadora de riesgo (plan v4.2 §7). Espejo de
 * `api-rendafin/src/modules/risk-scorecard-config/interfaces/risk-scorecard-params.interface.ts`.
 */

import { MetricState, RiskBand } from '@data/interfaces/risk-scorecard.interface'

export type PillarKey = 'character' | 'capacity' | 'capital'
export type Direction = 'higher_better' | 'lower_better'
export type ThresholdUnit = 'count' | 'years' | '%' | 'hhi' | 'pts' | 'x' | 'days' | 'mxn'
export type ConfigStatus = 'draft' | 'active' | 'archived'

export interface TierThresholds {
  t20: number
  t15: number
  t10: number
}

/** Cuántos puntos (sobre 20) vale cada nivel de tramo (plan v4.3 D-51). El alto siempre 20; por omisión 20/15/10/0. */
export interface TierPoints {
  t20: number
  t15: number
  t10: number
  t0: number
}

export interface TieredMetric {
  kind: 'tiered'
  pillar: PillarKey
  group?: string
  weight: number
  direction: Direction
  tiers: TierThresholds
  tierPoints?: TierPoints
  //* Opcional (H5): sin dato no cuenta en la cobertura.
  optional?: boolean
}

export interface GroupMetric {
  kind: 'group'
  pillar: PillarKey
  weight: number
}

export interface RuleMetric {
  kind: 'rule'
  pillar: PillarKey
  group?: string
  weight: number
  rule: string
  direction?: Direction
  tiers?: TierThresholds
  extra?: Record<string, TierThresholds>
  tierPoints?: TierPoints
}

export type MetricSpec = TieredMetric | GroupMetric | RuleMetric

//* Formato vigente de los parámetros (plan v4.3 T-03): una versión con otro es del motor 1 y la API no la usa.
export const SCORECARD_SCHEMA_VERSION = 2

export interface ScorecardParams {
  schemaVersion?: number
  //* Escala del Excel: los pesos efectivos suman `baseMax` (90) y el colateral se suma encima.
  scale?: { baseMax: number }
  pillars: Record<PillarKey, number>
  metrics: Record<string, MetricSpec>
  neutralTier: number
  partialCap: number
  partialCoverageFactor: number
  pillarMinCoverageBp: number
  bands: { favorableMin: number; reviewMin: number }
  collateral: { maxBonus: number; secondGuarantorPoints: number; solidGuaranteePoints: number }
  vetoes: Record<string, { enabled: boolean; band: 'review' | 'unfavorable' }>
  severeObservationKeys: string[]
  debtService: { revolvingFactor: number; perAccountCap: number }
  depreciation: { maxShareOfAvgGrossFixed: number }
  incompleteYear: { minShareOfCfdi: number }
  seriesTrim: { minShareOfMedian: number; maxMonths: number }
  creditHistory: { reuseDays: number; realMaxAgeDays: number; unitFactor: number; costPM: number | null; costPF: number | null }
  exposureTiers: { upTo: number | null; mode: 'informative' | 'binding' }[]
}

export interface Person {
  _id: string
  names?: string
  lastNames?: string
  email?: string
}

export interface ParamDiff {
  path: string
  previousValue: unknown
  newValue: unknown
}

export interface Activation {
  version: number
  previousVersion: number | null
  activatedAt: string
  activatedBy: { id: string; name: string } | null
  reason: string | null
}

export interface ConfigVersion {
  _id: string
  version: number
  name: string
  description: string
  status: ConfigStatus
  params?: ScorecardParams
  paramsHash: string
  derivedFromVersion: number | null
  changeReason: string
  diff: ParamDiff[]
  isSystemDefault: boolean
  createdBy: Person | null
  activatedBy: Person | null
  activatedAt: string | null
  archivedAt: string | null
  createdAt: string
  activations?: Activation[]
}

export interface ConfigCatalog {
  pillars: Record<PillarKey, string>
  groups: Record<string, string>
  metrics: Record<string, string>
  units: Record<string, ThresholdUnit>
  extraTiers: Record<string, { label: string; unit: ThresholdUnit }>
  rules: Record<string, string>
  vetoes: Record<string, string>
  bands: Record<string, string>
}

export interface ConfigDefaults {
  params: ScorecardParams
  catalog: ConfigCatalog
  reasonMinLength: number
}

/** La respuesta de un servicio del panel: nunca lanza; el error trae el `code` y la lista de problemas de la API. */
export interface ConfigResponse<T> {
  error: boolean
  msg: string
  data: T | null
  status: number
  code?: string
  errors?: string[]
}

// ── Simulador (plan v4.2 §10, E4) ─────────────────────────────────────────────

export type SimulationTier = 0 | 10 | 15 | 20

export interface SimulationDistribution {
  bands: Record<RiskBand, number>
  //* Por qué la banda no es la del puntaje: vetos y cobertura.
  reasons: Array<{ kind: 'veto' | 'coverage'; code: string; label: string; count: number }>
  metrics: Array<{ key: string; label: string; pillar: PillarKey; tiers: Record<SimulationTier, number>; states: Record<MetricState, number> }>
  //* Total (base + colateral) en la escala del Excel, que decide la banda; y la base sola.
  score: { mean: number; median: number; min: number; max: number } | null
  baseScore?: { mean: number; median: number; min: number; max: number } | null
  collateral?: { secondGuarantor: number; solidGuarantee: number }
  legalGate?: { withSignals: number; undetermined: number }
}

export interface SimulationBandChange {
  clientId: string
  clientName: string | null
  from: { band: RiskBand; score: number }
  to: { band: RiskBand; score: number }
}

/** `POST /risk-scorecard-config/simulate`: la activa y la candidata sobre las mismas fotos. */
export interface SimulationResult {
  sample: {
    considered: number
    companies: number
    individuals: number
    skipped: Record<string, number>
    failed: number
    demoExcluded: number
  }
  baseline: SimulationDistribution
  candidate: SimulationDistribution
  transitions: Array<{ from: RiskBand; to: RiskBand; count: number }>
  metricChanges: Record<string, number>
  changes: SimulationBandChange[]
  baselineConfig: { id: string | null; version: number }
  candidateConfig: { id: string | null; version: number | null }
  sampleSize: number
  computedAt: string
  //* Plan v4.3 §9.4 y §2b · Sobre los clientes nuevos con preoferta: además, a dónde irían con cada versión.
  population?: SimulationPopulation
  preoffers?: { minTotalScore: number; baseline: PreofferRouteTally; candidate: PreofferRouteTally }
}

/** Sobre quién se simula: la ficha de cada cliente con foto, o los clientes nuevos con su preoferta ya decidida. */
export type SimulationPopulation = 'clients' | 'new_clients'

export interface PreofferRouteTally {
  issued: number
  legalReview: number
  notEligible: number
  uncomputable: number
  reasons: Array<{ code: string; label: string; count: number }>
}

export type SimulationRequest = ({ configId: string; params?: never } | { params: ScorecardParams; configId?: never }) & { sampleSize?: number }
